import type * as FRAGS from "@thatopen/fragments";
import { getPresentationLayersForElement } from "./presentation-layers";

export type PropertyValue = string | number | boolean;
export type FormattedPropertySets = Record<
  string,
  Record<string, PropertyValue>
>;

const PRIORITY_SECTIONS = [
  "Reference Object",
  "Product",
  "Presentation Layers",
  "Ifc Material",
  "Spatial Container",
];

const REFERENCE_OBJECT_ATTRS = [
  "GlobalId",
  "Name",
  "Description",
  "ObjectType",
] as const;

const PRODUCT_ATTRS = ["Tag", "PredefinedType", "ElementType"] as const;

const ELEMENT_DATA_CONFIG: Partial<FRAGS.ItemsDataConfig> = {
  attributesDefault: true,
  relations: {
    HasAssociations: { attributes: true, relations: true },
    ContainedInStructure: { attributes: true, relations: true },
  },
};

const PROPERTY_SETS_QUERY_CONFIG: Partial<FRAGS.ItemsDataConfig> = {
  attributesDefault: false,
  attributes: ["Name", "NominalValue"],
  relations: {
    IsDefinedBy: { attributes: true, relations: true },
    DefinesOccurrence: { attributes: false, relations: false },
  },
};

export function extractFieldValue(field: unknown): PropertyValue | null {
  if (field == null) return null;
  if (
    typeof field === "string" ||
    typeof field === "number" ||
    typeof field === "boolean"
  ) {
    return field;
  }
  if (typeof field === "object" && "value" in field) {
    return extractFieldValue((field as { value: unknown }).value);
  }
  return null;
}

function getItemLocalId(item: FRAGS.ItemData): number | null {
  const id = extractFieldValue(item._localId);
  return typeof id === "number" ? id : null;
}

function isDirectPropertyContainer(item: FRAGS.ItemData): boolean {
  return (
    Array.isArray(item.HasProperties) ||
    Array.isArray(item.Quantities)
  );
}

function isIfcTypeItem(item: FRAGS.ItemData): boolean {
  const category = extractFieldValue(item._category);
  if (typeof category === "string" && /TYPE$/i.test(category)) return true;
  return !isDirectPropertyContainer(item) && getItemLocalId(item) != null;
}

function pickAttributes(
  data: FRAGS.ItemData,
  keys: readonly string[],
): Record<string, PropertyValue> {
  const props: Record<string, PropertyValue> = {};
  for (const key of keys) {
    const value = extractFieldValue(data[key]);
    if (value != null) props[key] = value;
  }
  return props;
}

function formatAssociationItem(item: FRAGS.ItemData): PropertyValue | null {
  const name = extractFieldValue(item.Name);
  if (name != null) return String(name);

  if (Array.isArray(item.MaterialLayers)) {
    const layers: string[] = [];
    for (const layer of item.MaterialLayers) {
      const material = Array.isArray(layer.Material)
        ? (layer.Material[0] as FRAGS.ItemData | undefined)
        : undefined;
      const layerName =
        extractFieldValue(material?.Name) ?? extractFieldValue(layer.Name);
      if (layerName != null) layers.push(String(layerName));
    }
    if (layers.length) return layers.join(", ");
  }

  const category = extractFieldValue(item._category);
  return category != null ? String(category) : null;
}

function formatMaterialSection(
  associations: FRAGS.ItemData[] | undefined,
): Record<string, PropertyValue> {
  const props: Record<string, PropertyValue> = {};
  if (!Array.isArray(associations)) return props;

  for (const [index, assoc] of associations.entries()) {
    const value = formatAssociationItem(assoc);
    if (value == null) continue;
    const label =
      extractFieldValue(assoc.Name) ??
      extractFieldValue(assoc._category) ??
      `Material ${index + 1}`;
    props[String(label)] = value;
  }

  return props;
}

function formatSpatialContainerSection(
  containers: FRAGS.ItemData[] | undefined,
): Record<string, PropertyValue> {
  const props: Record<string, PropertyValue> = {};
  if (!Array.isArray(containers)) return props;

  for (const [index, container] of containers.entries()) {
    const name = extractFieldValue(container.Name);
    const category = extractFieldValue(container._category);
    const label = index === 0 ? "Contained In" : `Contained In ${index + 1}`;
    if (name != null) props[label] = String(name);
    else if (category != null) props[label] = String(category);
  }

  return props;
}

function formatPresentationLayersSection(
  layerNames: string[],
): Record<string, PropertyValue> {
  const props: Record<string, PropertyValue> = {};
  for (const layerName of layerNames) {
    props[layerName] = layerName;
  }
  return props;
}

export async function getElementName(
  model: FRAGS.FragmentsModel,
  localId: number,
): Promise<string> {
  const [data] = await model.getItemsData([localId], {
    attributesDefault: false,
    attributes: ["Name"],
  });
  const name = extractFieldValue(data?.Name);
  if (name != null) return String(name);

  const category = extractFieldValue(data?._category);
  return category != null ? String(category) : `Element ${localId}`;
}

export async function getItemPropertySets(
  model: FRAGS.FragmentsModel,
  localId: number,
): Promise<FRAGS.ItemData[]> {
  const [data] = await model.getItemsData(
    [localId],
    PROPERTY_SETS_QUERY_CONFIG,
  );
  const items = data?.IsDefinedBy;
  return Array.isArray(items) ? items : [];
}

/**
 * Walks the element's `IsDefinedBy` graph breadth-first, batching all type
 * lookups at each level into a single worker query to avoid sequential
 * round-trips.
 */
async function collectIsDefinedByItems(
  model: FRAGS.FragmentsModel,
  localId: number,
): Promise<FRAGS.ItemData[]> {
  const collected: FRAGS.ItemData[] = [];
  const visited = new Set<number>([localId]);
  let frontier: number[] = [localId];

  while (frontier.length) {
    const dataList = await model.getItemsData(
      frontier,
      PROPERTY_SETS_QUERY_CONFIG,
    );

    const nextFrontier: number[] = [];
    for (const data of dataList) {
      const items = data?.IsDefinedBy;
      if (!Array.isArray(items)) continue;

      for (const item of items) {
        collected.push(item);

        if (isDirectPropertyContainer(item)) continue;
        if (!isIfcTypeItem(item)) continue;

        const typeLocalId = getItemLocalId(item);
        if (typeLocalId == null || visited.has(typeLocalId)) continue;
        visited.add(typeLocalId);
        nextFrontier.push(typeLocalId);
      }
    }

    frontier = nextFrontier;
  }

  return collected;
}

export function formatPropertySets(
  rawItems: FRAGS.ItemData[],
): FormattedPropertySets {
  const result: FormattedPropertySets = {};

  for (const item of rawItems) {
    const setName = extractFieldValue(item.Name);
    if (!setName) continue;

    const props: Record<string, PropertyValue> = {};

    if (Array.isArray(item.HasProperties)) {
      for (const prop of item.HasProperties) {
        const name = extractFieldValue(prop.Name);
        const value = extractFieldValue(prop.NominalValue);
        if (name != null && value != null) props[String(name)] = value;
      }
    }

    if (Array.isArray(item.Quantities)) {
      for (const qty of item.Quantities) {
        const name = extractFieldValue(qty.Name);
        const value =
          extractFieldValue(qty.VolumeValue) ??
          extractFieldValue(qty.AreaValue) ??
          extractFieldValue(qty.LengthValue) ??
          extractFieldValue(qty.CountValue) ??
          extractFieldValue(qty.WeightValue) ??
          extractFieldValue(qty.TimeValue);
        if (name != null && value != null) props[String(name)] = value;
      }
    }

    if (Object.keys(props).length) {
      const setKey = String(setName);
      result[setKey] = { ...result[setKey], ...props };
    }
  }

  return result;
}

export function mergeFormattedPropertySets(
  ...sources: FormattedPropertySets[]
): FormattedPropertySets {
  const result: FormattedPropertySets = {};
  for (const source of sources) {
    for (const [setName, props] of Object.entries(source)) {
      result[setName] = { ...result[setName], ...props };
    }
  }
  return result;
}

export async function getElementPropertySets(
  model: FRAGS.FragmentsModel,
  localId: number,
): Promise<FormattedPropertySets> {
  const [elementData, rawPsets, layerNames] = await Promise.all([
    model.getItemsData([localId], ELEMENT_DATA_CONFIG).then(([data]) => data),
    collectIsDefinedByItems(model, localId),
    getPresentationLayersForElement(model, localId),
  ]);

  const sections: FormattedPropertySets = {};

  if (elementData) {
    const guid =
      extractFieldValue(elementData.GlobalId) ??
      extractFieldValue(elementData._guid);
    const referenceProps: Record<string, PropertyValue> = {
      ...pickAttributes(elementData, REFERENCE_OBJECT_ATTRS),
    };
    if (guid != null && referenceProps.GlobalId == null) {
      referenceProps.GlobalId = guid;
    }
    const category = extractFieldValue(elementData._category);
    if (category != null) referenceProps.Category = String(category);

    if (Object.keys(referenceProps).length) {
      sections["Reference Object"] = referenceProps;
    }

    const productProps = pickAttributes(elementData, PRODUCT_ATTRS);
    if (Object.keys(productProps).length) {
      sections["Product"] = productProps;
    }

    const materialProps = formatMaterialSection(
      elementData.HasAssociations as FRAGS.ItemData[] | undefined,
    );
    if (Object.keys(materialProps).length) {
      sections["Ifc Material"] = materialProps;
    }

    const spatialProps = formatSpatialContainerSection(
      elementData.ContainedInStructure as FRAGS.ItemData[] | undefined,
    );
    if (Object.keys(spatialProps).length) {
      sections["Spatial Container"] = spatialProps;
    }
  }

  const layerProps = formatPresentationLayersSection(layerNames);
  if (Object.keys(layerProps).length) {
    sections["Presentation Layers"] = layerProps;
  }

  const psetSections = formatPropertySets(rawPsets);
  return mergeFormattedPropertySets(sections, psetSections);
}

export function sortPropertySetNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const aIndex = PRIORITY_SECTIONS.indexOf(a);
    const bIndex = PRIORITY_SECTIONS.indexOf(b);
    const aRank = aIndex === -1 ? PRIORITY_SECTIONS.length : aIndex;
    const bRank = bIndex === -1 ? PRIORITY_SECTIONS.length : bIndex;
    if (aRank !== bRank) return aRank - bRank;
    return a.localeCompare(b);
  });
}

export function filterPropertySets(
  propertySets: FormattedPropertySets,
  query: string,
): FormattedPropertySets {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return propertySets;

  const filtered: FormattedPropertySets = {};
  for (const [setName, props] of Object.entries(propertySets)) {
    const setMatches = setName.toLowerCase().includes(trimmed);
    const matchedProps: Record<string, PropertyValue> = {};

    for (const [propName, value] of Object.entries(props)) {
      const propMatches =
        propName.toLowerCase().includes(trimmed) ||
        String(value).toLowerCase().includes(trimmed);
      if (setMatches || propMatches) matchedProps[propName] = value;
    }

    if (Object.keys(matchedProps).length) filtered[setName] = matchedProps;
  }

  return filtered;
}

export function countSelectedElements(
  modelIdMap: Record<string, Set<number>>,
): number {
  let count = 0;
  for (const localIds of Object.values(modelIdMap)) {
    count += localIds.size;
  }
  return count;
}

export function getFirstSelectedElement(
  modelIdMap: Record<string, Set<number>>,
): { modelId: string; localId: number } | null {
  for (const [modelId, localIds] of Object.entries(modelIdMap)) {
    const localId = localIds.values().next().value;
    if (localId !== undefined) return { modelId, localId };
  }
  return null;
}
